/**
 * LeadKanbanColumn
 * Coluna do quadro de leads (um estágio); área de soltar do arrastar.
 */

import { useDroppable } from '@dnd-kit/core';
import { cn } from '@/utils/cn';
import { LeadStage, LEAD_STAGE_LABELS } from '../../types/leads';
import type { Lead } from '../../types/leads';
import { LeadKanbanCard } from './LeadKanbanCard';
import { formatLeadValue } from './lead-format';

export interface LeadKanbanColumnProps {
  stage: LeadStage;
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
  isArchived?: boolean;
}

export function LeadKanbanColumn({ stage, leads, onLeadClick, isArchived = false }: LeadKanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: stage, data: { type: 'column', stage } });
  const total = formatLeadValue(leads.reduce((sum, lead) => sum + (lead.estimatedValue || 0), 0));
  const label = LEAD_STAGE_LABELS[stage];

  return (
    <section
      ref={setNodeRef}
      aria-label={label}
      className={cn(
        'flex min-h-96 w-72 shrink-0 flex-col gap-2 rounded-lg bg-muted/40 p-2',
        isOver && 'ring-2 ring-primary/40',
        isArchived && 'opacity-75'
      )}
    >
      <header className="px-1">
        <h2 className="flex items-center justify-between text-sm font-semibold">
          {label}
          <span className="tabular-nums text-muted-foreground">{leads.length}</span>
        </h2>
        {total && <p className="text-xs tabular-nums text-muted-foreground">{total}</p>}
      </header>
      {leads.length === 0 ? (
        <p className={cn('py-6 text-center text-sm', isOver ? 'font-medium text-primary' : 'text-muted-foreground')}>
          {isOver ? 'Solte aqui' : 'Nenhum lead'}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {leads.map((lead) => (
            <LeadKanbanCard key={lead.id} lead={lead} onClick={() => onLeadClick(lead)} />
          ))}
        </div>
      )}
    </section>
  );
}

export default LeadKanbanColumn;
